import React, { useEffect, useState } from "react";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../firebase";
import "../css/settings.css";
import { useNavigate } from "react-router-dom";

function AdminSettings() {
  const [activeSection, setActiveSection] = useState("company");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [companyData, setCompanyData] = useState({
    companyName: "",
    companyLogo: "",
    customerWarrantyLabel: "",
    officeAddress: "",
    password: "",
    supportEmail: "",
    supportMobile: "",
    website: "",
  });

  const [logoPreview, setLogoPreview] = useState("");
  const [terms, setTerms] = useState([]);

  useEffect(() => {
    const fetchSettings = async () => {
      const storedAuth = sessionStorage.getItem("adminAuth");
      if (!storedAuth) {
        navigate("/admin");
        return;
      }

      const decodedPassword = atob(storedAuth);

      const settingsRef = doc(db, "app-settings", "app-settings");
      const settingsSnap = await getDoc(settingsRef);

      if (
        !settingsSnap.exists() ||
        settingsSnap.data().password !== decodedPassword
      ) {
        sessionStorage.removeItem("adminAuth");
        navigate("/");
        return;
      }

      const ref = doc(db, "app-settings", "app-settings");
      const snap = await getDoc(ref);

      if (snap.exists()) {
        const data = snap.data();

        setCompanyData({
          companyName: data.companyName || "",
          companyLogo: data.companyLogo || "",
          customerWarrantyLabel: data.customerWarrantyLabel || "",
          officeAddress: data.officeAddress || "",
          password: data.password || "",
          supportEmail: data.supportEmail || "",
          supportMobile: data.supportMobile || "",
          website: data.website || "",
        });

        setLogoPreview(data.companyLogo || "");
        setTerms(data["terms-and-conditions"] || []);
      }

      setLoading(false);
    };

    fetchSettings();
  }, [navigate]);

  const handleCompanyChange = (e) => {
    setCompanyData({ ...companyData, [e.target.name]: e.target.value });
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 300 * 1024) {
      alert("Image must be under 300KB");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result);
      setCompanyData((prev) => ({
        ...prev,
        companyLogo: reader.result,
      }));
    };
    reader.readAsDataURL(file);
  };

  const saveCompanyDetails = async () => {
    await setDoc(
      doc(db, "app-settings", "app-settings"),
      {
        ...companyData,
        "customer-warranty-label": companyData.customerWarrantyLabel,
      },
      { merge: true }
    );
    alert("Company details saved successfully!");
  };

  const addTerm = () => setTerms([...terms, ""]);

  const updateTerm = (index, value) => {
    const updated = [...terms];
    updated[index] = value;
    setTerms(updated);
  };

  const deleteTerm = (index) => {
    setTerms(terms.filter((_, i) => i !== index));
  };

  const saveTerms = async () => {
    await setDoc(
      doc(db, "app-settings", "app-settings"),
      { "terms-and-conditions": terms },
      { merge: true }
    );
    alert("Terms & Conditions saved successfully!");
  };

  const handleBackToUsers = () => {
    navigate("/admin/users-details");
  };

  if (loading) {
    return (
      <div className="settings-loader-container">
        <div className="settings-spinner"></div>
        <p>Loading settings...</p>
      </div>
    );
  }

  return (
    <div className="settings-container">
      {/* Header with Back Button */}
      <div className="settings-header">
        <button className="settings-back-btn" onClick={handleBackToUsers}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          Back to Users
        </button>
        <h1 className="settings-main-title">Admin Settings</h1>
      </div>

      <div className="settings-wrapper">
        {/* Sidebar */}
        <div className="settings-sidebar">
          <div className="sidebar-header">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="3"/>
              <path d="M12 1v6m0 6v6m-9-9h6m6 0h6"/>
            </svg>
            <h3>Settings</h3>
          </div>
          
          <button
            className={`sidebar-btn ${activeSection === "company" ? "active" : ""}`}
            onClick={() => setActiveSection("company")}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
              <polyline points="9 22 9 12 15 12 15 22"/>
            </svg>
            Company Details
          </button>
          
          <button
            className={`sidebar-btn ${activeSection === "terms" ? "active" : ""}`}
            onClick={() => setActiveSection("terms")}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1="16" y1="13" x2="8" y2="13"/>
              <line x1="16" y1="17" x2="8" y2="17"/>
              <polyline points="10 9 9 9 8 9"/>
            </svg>
            Terms & Conditions
          </button>
        </div>

        {/* Content */}
        <div className="settings-content">
          {activeSection === "company" && (
            <div className="settings-section">
              <div className="section-header">
                <h2>Company Details</h2>
                <p className="section-subtitle">Manage your company information and branding</p>
              </div>

              <div className="form-card">
                <div className="form-group">
                  <label>Company Name</label>
                  <input 
                    name="companyName" 
                    placeholder="Enter company name" 
                    value={companyData.companyName} 
                    onChange={handleCompanyChange} 
                  />
                </div>

                <div className="form-group logo-upload-section">
                  <label>Company Logo</label>
                  <div className="logo-upload-container">
                    {logoPreview && (
                      <div className="logo-preview-wrapper">
                        <img src={logoPreview} alt="Company Logo" className="logo-preview" />
                      </div>
                    )}
                    <div className="file-input-wrapper">
                      <input 
                        type="file" 
                        accept="image/*" 
                        onChange={handleLogoUpload}
                        id="logo-upload"
                      />
                      <label htmlFor="logo-upload" className="file-input-label">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                          <polyline points="17 8 12 3 7 8"/>
                          <line x1="12" y1="3" x2="12" y2="15"/>
                        </svg>
                        Choose Logo
                      </label>
                      <span className="file-input-hint">Max 300KB</span>
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <label>Customer Warranty Label</label>
                  <input 
                    name="customerWarrantyLabel" 
                    placeholder="Enter warranty label" 
                    value={companyData.customerWarrantyLabel} 
                    onChange={handleCompanyChange} 
                  />
                </div>

                <div className="form-group">
                  <label>Office Address</label>
                  <textarea 
                    name="officeAddress" 
                    placeholder="Enter office address" 
                    value={companyData.officeAddress} 
                    onChange={handleCompanyChange}
                    rows="3"
                  />
                </div>

                <div className="form-group">
                  <label>Admin Password</label>
                  <input 
                    name="password" 
                    type="password"
                    placeholder="Enter admin password" 
                    value={companyData.password} 
                    onChange={handleCompanyChange} 
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Support Email</label>
                    <input 
                      name="supportEmail" 
                      type="email"
                      placeholder="support@company.com" 
                      value={companyData.supportEmail} 
                      onChange={handleCompanyChange} 
                    />
                  </div>

                  <div className="form-group">
                    <label>Support Mobile</label>
                    <input 
                      name="supportMobile" 
                      type="tel"
                      placeholder="+1234567890" 
                      value={companyData.supportMobile} 
                      onChange={handleCompanyChange} 
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Website</label>
                  <input 
                    name="website" 
                    type="url"
                    placeholder="https://www.company.com" 
                    value={companyData.website} 
                    onChange={handleCompanyChange} 
                  />
                </div>
              </div>

              <button className="save-btn primary" onClick={saveCompanyDetails}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
                  <polyline points="17 21 17 13 7 13 7 21"/>
                  <polyline points="7 3 7 8 15 8"/>
                </svg>
                Save Company Details
              </button>
            </div>
          )}

          {activeSection === "terms" && (
            <div className="settings-section">
              <div className="section-header">
                <h2>Terms & Conditions</h2>
                <p className="section-subtitle">Define rules and conditions for your warranty claims</p>
              </div>

              <div className="terms-list">
                {terms.length === 0 ? (
                  <div className="empty-state">
                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                      <polyline points="14 2 14 8 20 8"/>
                    </svg>
                    <p>No terms added yet</p>
                  </div>
                ) : (
                  terms.map((term, index) => (
                    <div key={index} className="term-card">
                      <div className="term-number">{index + 1}</div>
                      <input
                        value={term}
                        placeholder={`Enter rule ${index + 1}`}
                        onChange={(e) => updateTerm(index, e.target.value)}
                        className="term-input"
                      />
                      <button className="delete-term-btn" onClick={() => deleteTerm(index)}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3 6 5 6 21 6"/>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                          <line x1="10" y1="11" x2="10" y2="17"/>
                          <line x1="14" y1="11" x2="14" y2="17"/>
                        </svg>
                      </button>
                    </div>
                  ))
                )}
              </div>

              <button className="add-term-btn" onClick={addTerm}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="8" x2="12" y2="16"/>
                  <line x1="8" y1="12" x2="16" y2="12"/>
                </svg>
                Add New Rule
              </button>

              <button className="save-btn primary" onClick={saveTerms}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
                  <polyline points="17 21 17 13 7 13 7 21"/>
                  <polyline points="7 3 7 8 15 8"/>
                </svg>
                Save Terms & Conditions
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminSettings;